import { asArray, asObject, cloneValue, text } from './model-utils.js';
import { buildChapters } from './chapter-builders.js';
import { createAssetIndex, DOCUMENT_SCHEMA_VERSION, normalizeStages } from './document-model.js';

/** @typedef {ReturnType<typeof normalizeProject>} DeliverablesDocument */

/**
 * Converts a main-project-shaped snapshot into the stable 13-chapter document.
 * The source snapshot is never mutated.
 *
 * Strict inclusion rules:
 * - stage 02 conditions must be completed and an individual condition must have
 *   `userConfirmed === true` before entering the confirmed constraints table;
 * - stage 03-05 content must be completed;
 * - image content must be linked by an explicit asset id and exist in assets.
 *
 * @param {Record<string, any>} snapshot
 */
export function normalizeProject(snapshot) {
  const source = cloneValue(asObject(snapshot));
  const rawProject = asObject(source.project);
  const project = {
    id: text(rawProject.id, 'unknown-project'),
    name: text(rawProject.name, '未命名项目'),
    type: text(rawProject.type || rawProject.projectType || rawProject.project_type),
    location: text(rawProject.location),
    description: text(rawProject.description),
    progress: Math.max(0, Math.min(100, Number(rawProject.progress) || 0)),
    currentStage: text(rawProject.currentStage || rawProject.current_stage),
    updatedAt: text(rawProject.updatedAt || rawProject.updated_at)
  };
  const stages = normalizeStages(source.stages);
  const materials = asArray(source.materials?.materials || source.materials);
  const assets = asArray(source.assets?.assets || source.assets);
  const context = { project, stages, materials, assets, assetIndex: createAssetIndex(assets) };

  return {
    schemaVersion: DOCUMENT_SCHEMA_VERSION,
    project,
    chapters: buildChapters(context),
    source: {
      kind: text(source.source?.kind, 'project-snapshot'),
      snapshotVersion: source.version || source.schemaVersion || 1,
      normalizedAt: text(source.normalizedAt)
    }
  };
}
